import { LoginModule } from './login/login.module';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, ErrorHandler, NgZone, InjectionToken } from '@angular/core';
import { TranslateModule, TranslateLoader, TranslateService, MissingTranslationHandler, TranslateCompiler } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppComponent } from './app.component';
import { Actions, NgxsModule, NGXS_PLUGINS, Store } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { AuthState } from './core/auth/auth.state';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { routing } from './app.routing';
import { APIInterceptor } from './core/api.interceptor';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { AuthGuard } from './core/auth/auth.guard';
import { environment } from 'environments/environment';
import { ResetPasswordModule } from './reset-password/reset-password.module';
import { clearStatePlugin } from './core/middleware/clear-state.plugin';
import { ForgotPasswordModule } from './forgot-password/forgot-password.module';
import { HttpErrorInterceptor } from './core/http-error.interceptor';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { UserInitializer } from './core/user.initializer';
import { OaErrorHandler } from './main/core/oa-error-handler';
import { ValidSportGuard } from './main/core/guards/valid-sport.guard';
import { VERSION } from '../environments/version';
import { SportInitializer } from './core/sport.initializer';
import { SportState } from './main/core/sport';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatAccordionBase, MatExpansionPanelDefaultOptions } from '@angular/material/expansion';
import { DateAdapter, MatNativeDateModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { OaMissingTranslationHandler } from './core/i18n/translate.utils';
import { Router } from '@angular/router';
import { AuthService } from './core/auth/auth.service';
import { SportService } from './main/core/sport/sport.service';
import { MESSAGE_FORMAT_CONFIG, TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { UserService } from './main/core/user/user.service';
import { MomentDateAdapter, MatMomentDateModule } from '@angular/material-moment-adapter';
import { OA_MOMENT_DATE_FORMAT } from './main/core/oa.generic.models';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CompanyService } from './main/core/company/company.service';
import { EnvironmentTiers } from 'environments/environment.models';
import { UserSettingsState } from './main/core/user-settings/user-settings.state';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { MAT_PAGINATOR_DEFAULT_OPTIONS } from '@angular/material/paginator';
import { PAGINATOR_DEFAULT_PAGE_SIZE_OPTIONS } from './core/constants';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MAT_LIST_CONFIG } from '@angular/material/list';
import { datadogRum } from '@datadog/browser-rum';

datadogRum.init({
    applicationId: '11978b4f-5c5e-4b78-9288-57981defa37a',
    clientToken: 'pub10df0b61284d420666077c1fc76ddf17',
    site: 'datadoghq.com',
    service: 'coaching-oneapp',
    version: '1.0.0',
    env: environment.tier,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackBfcacheViews: true,
    defaultPrivacyLevel: 'mask-user-input',
});

export function initUserFactory(userInitializer: UserInitializer, sportInitializer: SportInitializer) {
    return () => userInitializer.initializeUser().then(() =>
        // ensures that the sport initializer runs after the user initializer
        sportInitializer.initializeSport()
    );
}

export function initSportFactory(sportInitializer: SportInitializer) {
    return () => sportInitializer.initializeSport();
}
export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${VERSION.raw}`);
}

export const httpInterceptors = [
    {
        provide: HTTP_INTERCEPTORS,
        useClass: APIInterceptor,
        multi: true,
        deps: [Actions]
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpErrorInterceptor,
        multi: true,
        deps: [MatSnackBar, NgZone, TranslateService, Store, Router]
    }
];

// Token used to provide a MatAccordion to MatExpansionPanel.
// Used primarily to avoid circular imports between MatAccordion and MatExpansionPanel.
export const MAT_ACCORDION = new InjectionToken<MatAccordionBase>('MAT_ACCORDION');
export const MAT_EXPANSION_PANEL_DEFAULT_OPTIONS: InjectionToken<MatExpansionPanelDefaultOptions>
    = new InjectionToken<MatExpansionPanelDefaultOptions>('MAT_EXPANSION_PANEL_DEFAULT_OPTIONS');

@NgModule(
    { declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent],
    imports: [TranslateModule.forRoot({
        loader: {
            provide: TranslateLoader,
            useFactory: (createTranslateLoader),
            deps: [HttpClient]
        },
        compiler: {
            provide: TranslateCompiler,
            useClass: TranslateMessageFormatCompiler
        },
        missingTranslationHandler: { provide: MissingTranslationHandler, useClass: OaMissingTranslationHandler }
    }),
    NgxsModule.forRoot([
        AuthState,
        SportState,
        UserSettingsState
    ], { developmentMode: environment.tier !== EnvironmentTiers.PRODUCTION }),
    NgxsFormPluginModule.forRoot(),
    NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.tier === EnvironmentTiers.PRODUCTION }),
    NgxsRouterPluginModule.forRoot(),
    LoginModule,
    ResetPasswordModule,
    ForgotPasswordModule,
    routing,
    MatSnackBarModule,
    BrowserModule,
    BrowserAnimationsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatMomentDateModule],
    providers: [
        ...httpInterceptors,
        { provide: APP_INITIALIZER, useFactory: initUserFactory, deps: [UserInitializer, SportInitializer], multi: true },
        {
            provide: NGXS_PLUGINS,
            useValue: clearStatePlugin,
            multi: true
        },
        {
            provide: ErrorHandler,
            useClass: OaErrorHandler
        },
        ValidSportGuard,
        UserInitializer,
        SportInitializer,
        AuthGuard,
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                appearance: 'fill',
                color: 'primary',
                hideRequiredMarker: false,
                floatLabel: 'always',
                subscriptSizing: 'fixed'
            }
        },
        {
            provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS,
            useValue: {
                color: 'primary'
            }
        },
        {
            provide: MAT_ACCORDION,
            useValue: {
                displayMode: 'flat'
            }
        },
        {
            provide: MAT_RIPPLE_GLOBAL_OPTIONS,
            useValue: {
                disabled: true,
                animation: {
                    enterDuration: 0,
                    exitDuration: 0
                }
            }
        },
        {
            provide: MAT_RADIO_DEFAULT_OPTIONS,
            useValue: {
                color: 'primary'
            }
        },
        {
            provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
            useValue: {
                pageSizeOptions: PAGINATOR_DEFAULT_PAGE_SIZE_OPTIONS,
                showFirstLastButtons: true,
                formFieldAppearance: 'fill'
            }
        },
        {
            provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
            useValue: {
                color: 'primary'
            }
        },
        {
            provide: MAT_TABS_CONFIG,
            useValue: {
                animationDuration: '0'
            }
        },
        {
            provide: MAT_SELECT_CONFIG,
            useValue: {
                overlayPanelClass: 'mat-mdc-select__panel',
                disableOptionCentering: true
            }
        },
        {
            provide: MAT_LIST_CONFIG,
            useValue: {
                hideSingleSelectionIndicator: true
            }
        },
        AuthService,
        SportService,
        UserService,
        CompanyService,
        { provide: MESSAGE_FORMAT_CONFIG, useValue: { locales: ['en'] } },
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: OA_MOMENT_DATE_FORMAT },
        provideEnvironmentNgxMask(),
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
